// AssetGrid — grid of asset cards for the current folder. Supports:
//
// - Multi-select: click toggles single; Ctrl/Cmd-click toggles in/out of
//   the current selection; Shift-click selects a range from the anchor.
// - Bulk actions: when ≥2 are selected, a selection bar appears above the
//   grid with Delete / Move-to-Root / Clear.
// - Global search: a "search all folders" toggle widens the visible set to
//   the whole registry (vs the current folder), useful for hunting an
//   asset by name when you don't remember where you put it.
// - OS-file drag-drop into the grid uploads to the current folder.

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { assetRegistry } from '../../lib/assetRegistry'
import { loadBlueprintAssetGraph } from '../../lib/loadBlueprint'
import { downloadBlueprintCard } from '../../lib/blueprintAssetTransfer'
import { menubarCommands } from '../../lib/menubarCommands'
import { openGraphSurface } from '../../lib/assetKindColors'
import { withMinVisible } from '../../lib/minVisibleDuration'
import { resolveCharacterId } from '../../lib/characterState'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { previewAnimationAsset } from '../../lib/previewAnimationAsset'
import { EventRuntime } from '../../lib/EventRuntime'
import { reloadMaterialsEverywhere } from '../../lib/materialReloadBus'
import { applyEnvironment } from '../../lib/applyEnvironment'
import { assetViewMode, VIEW_MODES, VIEW_METRICS } from '../../lib/assetViewMode.js'
import { isHiddenFolderKey, isHiddenFolderSegments, showHiddenFolders } from '../../lib/hiddenFolders'
import AssetCard from './AssetCard'
import AssetContextMenu from './AssetContextMenu'
import ItemHistoryModal from '../versionControl/ItemHistoryModal.jsx'
import { assetKindToSection } from '../../lib/versionHistory.js'
import FolderPickerModal from './FolderPickerModal'
import PrefabEditModal from './PrefabEditModal'
import { dialog } from '../../lib/dialog'
import { renameAsset, isRenameableAsset } from '../../lib/renameAsset'
import { pill, menuSurface, activeWash } from '../../lib/studioStyles.js'

const C = {
  bg:       'var(--m-bg, #0c0e15)',
  text:     'var(--m-fg, #e1e1ed)',
  dim:      'var(--m-fg-2, #8f8fa2)',
  // The old alpha-suffix derivation (`C.primary + '22'`) is gone — active
  // toggles now ride var(--m-accent-soft) — so primary can be a token again.
  primary:  'var(--m-accent, #bec2ff)',
  border:   'var(--m-line, #1f2129)',
  selBar:   'var(--m-panel-hi, #1a1f3a)',
  danger:   '#f87171',
}

const KIND_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'body_anim', label: 'Body' },
  { key: 'face_anim', label: 'Face' },
  { key: 'audio',     label: 'Audio' },
  { key: 'prefab',    label: 'Prefab' },
  { key: 'material',  label: 'Material' },
  { key: 'texture',   label: 'Texture' },
  { key: 'environment', label: 'Environment' },
  { key: 'ogs',       label: 'OGS' },
  { key: 'blueprint_asset', label: 'Blueprint' },
]

// Shared kind-dispatch for delete — also used by ContentBrowser's toolbar trash.
export async function deleteOne(asset) {
  if (asset.kind === 'audio')                          await CustomAssetManager.deleteAudioAsset(asset.rawId)
  else if (asset.kind === 'body_anim' ||
           asset.kind === 'face_anim')                 await CustomAssetManager.deleteAnimationAsset(asset.rawId)
  else if (asset.kind === 'prefab')                    await CustomAssetManager.deletePrefab(asset.rawId)
  else if (asset.kind === 'material')                  await CustomAssetManager.deleteMaterial(asset.rawId)
  else if (asset.kind === 'environment')               await CustomAssetManager.deleteEnvironmentAsset(asset.rawId)
  else if (asset.kind === 'ogs')                       await CustomAssetManager.deleteOgsAsset(asset.rawId)
  else if (asset.kind === 'texture' && !asset.rawId.includes('.')) await CustomAssetManager.deleteTextureAsset(asset.rawId)
  // The character's DEFAULT blueprint is refused by the server (409) — it is
  // what the character loads. The rejection propagates to the caller's error
  // handling rather than being swallowed into a card that quietly stays.
  else if (asset.kind === 'blueprint_asset')           await CustomAssetManager.deleteBlueprintAsset(asset.rawId)
  else console.warn(`[deleteOne] no delete wired for kind "${asset.kind}"`)
}

export default function AssetGrid({ activePath, onUploadFiles, selectedAssetId, onSelectAsset, onNavigate, onDropAsset, onEmptyContextMenu, onOpenGraph, characterId }) {
  const [, force] = useState(0)
  const [filterKind, setFilterKind] = useState('all')
  // Layout mode (large icons / small icons / details), PlayCanvas's three.
  const [viewMode, setViewMode] = useState(assetViewMode.get())
  useEffect(() => assetViewMode.subscribe(setViewMode), [])
  const metrics = VIEW_METRICS[viewMode]
  // The type filter is a dropdown rather than a chip per kind: nine chips ate the
  // toolbar at dock widths, and PlayCanvas's own panel uses a single
  // "Filter by Type" select for the same reason.
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRootRef = useRef(null)
  useEffect(() => {
    if (!filterOpen) return
    const close = (e) => { if (!filterRootRef.current?.contains(e.target)) setFilterOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [filterOpen])
  const [query, setQuery] = useState('')
  const [searchAll, setSearchAll] = useState(false)
  const [sortBy, setSortBy] = useState(() => {
    if (typeof localStorage !== 'undefined') return localStorage.getItem('contentBrowser.sort') || 'name'
    return 'name'
  })
  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('contentBrowser.sort', sortBy)
  }, [sortBy])
  const [over, setOver] = useState(false)
  // Multi-select: ordered list of registry ids (kept in click order for
  // Shift-range anchoring).
  const [multi, setMulti] = useState(() => new Set())
  const [anchorId, setAnchorId] = useState(null)
  const [showMovePicker, setShowMovePicker] = useState(false)
  const [ctxMenu, setCtxMenu] = useState(null) // { asset, x, y } | null
  const [renamingId, setRenamingId] = useState(null) // asset.id being inline-renamed | null
  const [itemHistory, setItemHistory] = useState(null) // { section, itemId, label } | null
  const [editingPrefab, setEditingPrefab] = useState(null) // full prefab record being edited | null
  // The card whose open is in flight. Hydration is often <100ms warm, so this
  // is held for a floor by withMinVisible — the card's bounce is the answer to
  // "the double-click landed", which a flag that unmounts in two frames never
  // gives. One id, not a set: opening a second card supersedes the first
  // (loadBlueprintAssetGraph already reports `superseded` for that race).
  const [openingId, setOpeningId] = useState(null)

  // Open the prefab editor for a content-browser prefab card (double-click or
  // right-click → Edit). Resolve the freshest record from CustomAssetManager
  // rather than trusting the registry projection's snapshot.
  // Open one blueprint row's graph: hydrate it, then show that surface — the
  // same two steps the inspector button and the outliner row perform.
  // Export ONE blueprint card to a file (ENG-1030 asset model). The legacy
  // File-menu export composes all three graphs out of the localStorage editor
  // caches; this reads the SERVER row for exactly this card — the same source
  // its own Duplicate uses, and the only one the server-is-truth rule permits.
  const exportBlueprintCard = useCallback(async (asset) => {
    try {
      const { fileName } = await downloadBlueprintCard(asset, {
        character: characterId ?? resolveCharacterId(),
      })
      setActionStatus({ phase: 'ok', text: `Exported ${fileName}` })
    } catch (e) {
      // Loud: an export that quietly wrote nothing would only be discovered on
      // the import side, on another character, later.
      setActionStatus({ phase: 'error', text: e?.message ?? String(e) })
    }
  }, [characterId])

  const openBlueprintGraph = useCallback(async (asset) => {
    if (asset?.kind !== 'blueprint_asset' || !asset.graphKind) return
    // withMinVisible raises the flag BEFORE the first await, so the bounce
    // starts on the same frame as the double-click rather than after the
    // fetch — the acknowledgement cannot lag the gesture that caused it.
    await withMinVisible(async () => {
      try {
        const res = await loadBlueprintAssetGraph(asset.rawId, characterId ?? resolveCharacterId(), asset.graphKind)
        if (res == null) {
          setActionStatus({ phase: 'error', text: `"${asset.name}" has no nodes — not opened` })
          return
        }
        if (!openGraphSurface(asset.graphKind, menubarCommands)) {
          setActionStatus({ phase: 'ok', text: `Loaded "${asset.name}" — open the Blueprint editor to see it` })
        }
      } catch (e) {
        setActionStatus({ phase: 'error', text: e?.message ?? String(e) })
      }
    }, (on) => setOpeningId(on ? asset.id : (cur) => (cur === asset.id ? null : cur)))
  }, [characterId])

  const openPrefabEditor = useCallback((asset) => {
    if (asset?.kind !== 'prefab') return
    const id = asset.rawId ?? asset.id
    const rec = CustomAssetManager.listPrefabs().find(p => p.id === id) ?? asset.raw
    if (rec) setEditingPrefab(rec)
  }, [])
  // Transient status chip for right-click actions whose result isn't visible
  // in this view (e.g. Apply-to-slot doesn't have a DirtyBadge nearby).
  // Shape: { phase: 'saving'|'ok'|'error', text }. Auto-clears on success.
  const [actionStatus, setActionStatus] = useState(null)

  useEffect(() => assetRegistry.subscribe(() => force(n => n + 1)), [])
  // When the active folder changes, drop the multi-selection (the visible
  // set is different now; carrying invisible selections is confusing).
  useEffect(() => { setMulti(new Set()); setAnchorId(null); setRenamingId(null) }, [activePath.join('/')])

  // Commit an inline rename (right-click → Rename / F2). Routes through the
  // shared per-kind renameAsset; a duplicate name hits the same unique
  // constraint as create, so surface that via the status chip rather than
  // leaving the card silently stuck.
  const commitRename = useCallback(async (asset, newName) => {
    setRenamingId(null)
    if (!newName || newName === asset.name) return
    setActionStatus({ phase: 'saving', text: `Renaming to "${newName}"…` })
    try {
      await renameAsset(asset, newName)
      setActionStatus({ phase: 'ok', text: `Renamed to "${newName}"` })
      setTimeout(() => setActionStatus(s => (s && s.phase === 'ok' ? null : s)), 1800)
    } catch (e) {
      setActionStatus({ phase: 'error', text: `Rename failed: ${e?.message ?? e}` })
      setTimeout(() => setActionStatus(s => (s && s.phase === 'error' ? null : s)), 5000)
    }
  }, [])

  // F2 renames the single selected asset (matching the folder tree + the menu
  // hint). Ignore while typing in a field or when a multi-selection is active.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'F2' || renamingId || multi.size > 1) return
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      const sel = assetRegistry.list().find(a => a.id === selectedAssetId)
      if (sel && isRenameableAsset(sel)) { e.preventDefault(); setRenamingId(sel.id) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [selectedAssetId, renamingId, multi])

  // Keyboard shortcuts. Scoped to the grid container so they don't fight
  // with the blueprint editor's own handlers. Wire on the document but
  // bail when a form input is focused.
  //
  // Subscribed ONCE: the handler reads the latest render's multi / visible /
  // bulkDelete through `keyCtxRef` (assigned every render, below, after those
  // values exist). The previous version had no dep array at all, so it tore
  // down and re-registered the document listener on every render.
  const keyCtxRef = useRef(null)
  useEffect(() => {
    const onKey = e => {
      const { multi, visible, bulkDelete } = keyCtxRef.current ?? {}
      if (!multi) return
      const tag = (e.target?.tagName ?? '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      if (e.key === 'Escape' && multi.size) {
        setMulti(new Set())
        return
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        // Select all visible (the current filtered + sorted set).
        e.preventDefault()
        setMulti(new Set(visible.map(a => a.id)))
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && multi.size) {
        e.preventDefault()
        bulkDelete()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const all = assetRegistry.list()
  const pathKey = activePath.join('/')
  // Reserved dot-folders (the interop '/.e2e' corral) stay hidden even in
  // global search — see hiddenFolders.js for the escape hatch.
  const revealHidden = showHiddenFolders()
  let visible = all.filter(a => {
    if (!revealHidden && isHiddenFolderSegments(a.path)) return false
    if (filterKind !== 'all' && a.kind !== filterKind) return false
    if (query && !a.name.toLowerCase().includes(query.toLowerCase())) return false
    if (!searchAll && (a.path ?? []).join('/') !== pathKey) return false
    return true
  })

  // Direct child folders of the active folder, shown as navigable tiles
  // (Explorer / Unreal style) so selecting a parent reveals its subfolders
  // instead of only its files. Hidden in global-search mode (no single path).
  const childFolders = (searchAll || query) ? [] : assetRegistry.listFolders()
    .filter(k => {
      if (!k) return false
      if (!revealHidden && isHiddenFolderKey(k)) return false
      const parts = k.split('/')
      return parts.length === activePath.length + 1 &&
             parts.slice(0, activePath.length).join('/') === pathKey
    })
    .map(k => {
      const parts = k.split('/')
      const prefix = k + '/'
      const count = all.filter(a => {
        const p = (a.path ?? []).join('/')
        return p === k || p.startsWith(prefix)
      }).length
      return { key: k, name: parts[parts.length - 1], path: parts, count }
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  // Stable sort. Names use case-insensitive locale compare; date uses the
  // raw created_at (newest first); kind groups same-kind together.
  visible = [...visible].sort((a, b) => {
    if (sortBy === 'date') {
      const da = new Date(a.raw?.created_at ?? 0).getTime()
      const db = new Date(b.raw?.created_at ?? 0).getTime()
      return db - da
    }
    if (sortBy === 'kind') {
      const cmp = (a.kind ?? '').localeCompare(b.kind ?? '')
      if (cmp !== 0) return cmp
    }
    return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
  })

  // ── Click handlers (multi-select aware) ──
  const handleCardClick = useCallback((asset, event) => {
    const id = asset.id
    if (event?.shiftKey && anchorId) {
      // Range select from anchor to id within visible order.
      const ids = visible.map(v => v.id)
      const a = ids.indexOf(anchorId)
      const b = ids.indexOf(id)
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a]
        setMulti(new Set(ids.slice(lo, hi + 1)))
        onSelectAsset?.(id)
        return
      }
    }
    if (event?.ctrlKey || event?.metaKey) {
      setMulti(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id); else next.add(id)
        return next
      })
      setAnchorId(id)
      onSelectAsset?.(id)
      return
    }
    // Plain click: single select; clear multi.
    setMulti(new Set([id]))
    setAnchorId(id)
    onSelectAsset?.(id)
  }, [anchorId, visible, onSelectAsset])

  const handleDrop = e => {
    e.preventDefault(); setOver(false)
    // In-app asset drag wins over synthetic files (see FolderTree.handleDrop
    // for the underlying bug — inner <img> drags injected fake JPGs into
    // dataTransfer.files). Dropping an asset card on the grid is a no-op
    // (the asset is already here); we just ignore it instead of treating
    // the synthetic image as a new upload.
    if (e.dataTransfer.getData('text/aos-asset-id')) return
    if (e.dataTransfer.files?.length) {
      onUploadFiles?.(Array.from(e.dataTransfer.files), activePath)
    }
  }

  const testAsset = (a) => {
    if (a.kind === 'audio' && a.url) {
      new Audio(a.url).play().catch(e => console.warn('audio play failed', e))
    } else if (a.kind === 'body_anim' || a.kind === 'face_anim') {
      // Resolve the clip's actual runtime key (seeded clips register under their
      // name, custom uploads under custom_<kind>_<id>) and play it the same way
      // the Inspector preview does. Hardcoding the alias here used to make Test
      // a no-op for every seeded clip.
      previewAnimationAsset(a, { animationAssets: CustomAssetManager.listAnimationAssets() })
    } else if (a.kind === 'prefab') {
      // Play the DB-backed prefab directly, the same path the Prefab Editor
      // "Test" button uses. The old window.__TEST_PREFAB__ hook fired the
      // legacy onPrefabTrigger event, which only the conversational.json
      // blueprint handled (seeded clips only) — a no-op for custom prefabs,
      // and undefined entirely unless the AnimTestPanel was mounted.
      EventRuntime.playPrefabByTag(a.tag)
    }
  }

  const deleteSingle = async (a) => {
    const ok = await dialog.confirm({
      title: 'Delete asset?',
      body: `Delete "${a.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    })
    if (!ok) return
    try { await deleteOne(a) }
    catch (e) {
      await dialog.alert({
        title: 'Delete failed',
        body: e?.message ?? String(e),
        variant: 'error',
      })
    }
  }

  const bulkDelete = async () => {
    const ids = Array.from(multi)
    if (!ids.length) return
    const ok = await dialog.confirm({
      title: `Delete ${ids.length} asset${ids.length > 1 ? 's' : ''}?`,
      body: 'This can\'t be undone.',
      confirmLabel: 'Delete all',
      danger: true,
    })
    if (!ok) return
    const assets = ids.map(id => all.find(a => a.id === id)).filter(Boolean)
    let failed = 0
    for (const a of assets) {
      try { await deleteOne(a) } catch (e) { console.error(`[bulk-delete] ${a.name}:`, e); failed++ }
    }
    setMulti(new Set())
    if (failed) {
      await dialog.alert({
        title: 'Some deletions failed',
        body: `${failed} of ${assets.length} deletions failed — check the console for details.`,
        variant: 'error',
      })
    }
  }

  // Multi-select → "Move to…". This used to call setPath, which writes only the
  // localStorage shadow: the cards moved and NOTHING was persisted, for any
  // asset kind. moveAsset persists and badges each failure on its own card.
  const bulkMoveTo = (path) => {
    const ids = [...multi]
    setMulti(new Set())
    setShowMovePicker(false)
    Promise.all(ids.map(id => assetRegistry.moveAsset(id, path))).then(results => {
      const failed = results.filter(r => r && !r.ok).length
      if (failed) {
        setActionStatus({
          phase: 'error',
          text: `${failed} of ${ids.length} moves did not persist — see the badge on each card`,
        })
        setTimeout(() => setActionStatus(s => (s && s.phase === 'error' ? null : s)), 6000)
      }
    })
  }

  const multiCount = multi.size

  // Latest render's values for the once-subscribed keyboard handler above.
  keyCtxRef.current = { multi, visible, bulkDelete }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      style={{
        flex: 1,
        background: C.bg,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        outline: over ? `2px dashed ${C.primary}` : 'none',
        outlineOffset: -4,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
      }}>
        <Breadcrumb path={activePath} searchAll={searchAll} onNavigate={onNavigate} />
        <div style={{ flex: 1 }} />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search…"
          style={{
            background: 'var(--m-panel-hi, #080a10)', border: `1px solid ${C.border}`,
            color: C.text, fontFamily: 'var(--m-font)', fontSize: 11,
            borderRadius: 999, padding: '4px 12px', outline: 'none',
            width: 160,
          }}
        />
        <button
          onClick={() => setSearchAll(s => !s)}
          title="Search all folders"
          style={toggleStyle(searchAll)}
        >Global</button>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          title="Sort"
          style={{
            background: 'var(--m-panel-hi, #080a10)', border: `1px solid ${C.border}`,
            color: C.dim, fontFamily: 'var(--m-font)', fontSize: 10,
            borderRadius: 999, padding: '3px 8px', outline: 'none',
            letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <option value="name">Sort: Name</option>
          <option value="date">Sort: Newest</option>
          <option value="kind">Sort: Kind</option>
        </select>
        {/* View mode — large icons / small icons / details, PlayCanvas's three
            (asset-panel.ts VIEW_LARGE_GRID / VIEW_SMALL_GRID / VIEW_DETAILS). */}
        <div style={{ display: 'flex', gap: 2 }}>
          {VIEW_MODES.map(m => (
            <button
              key={m}
              data-testid={`asset-view-${m}`}
              data-active={viewMode === m ? '1' : '0'}
              onClick={() => assetViewMode.set(m)}
              title={VIEW_METRICS[m].label}
              style={{
                ...toggleStyle(viewMode === m),
                width: 24, padding: 0, lineHeight: '18px', fontSize: 11,
              }}
            >{VIEW_METRICS[m].glyph}</button>
          ))}
        </div>

        {/* Filter by Type */}
        <div ref={filterRootRef} style={{ position: 'relative' }}>
          <button
            data-testid="asset-filter-button"
            onClick={() => setFilterOpen(o => !o)}
            title="Filter by Type"
            style={{ ...toggleStyle(filterKind !== 'all'), minWidth: 92, textAlign: 'left' }}
          >
            {(KIND_OPTIONS.find(o => o.key === filterKind) ?? KIND_OPTIONS[0]).label}
            <span style={{ float: 'right', opacity: 0.7 }}>{filterOpen ? '▴' : '▾'}</span>
          </button>
          {filterOpen && (
            <div
              data-testid="asset-filter-menu"
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
                minWidth: 132, maxHeight: 260, overflowY: 'auto',
                background: 'var(--m-panel)', border: `1px solid ${C.borderHi ?? C.border}`,
                ...menuSurface, padding: '4px 0',
              }}
            >
              <div style={{
                fontFamily: 'var(--m-font)', fontSize: 8.5, letterSpacing: 'var(--m-head-ls)',
                fontWeight: 700,
                textTransform: 'uppercase', color: C.dim, padding: '2px 10px 4px',
              }}>Filter by Type</div>
              {KIND_OPTIONS.map(o => {
                const on = filterKind === o.key
                return (
                  <div
                    key={o.key}
                    data-testid={`asset-filter-item-${o.key}`}
                    onClick={() => { setFilterKind(o.key); setFilterOpen(false) }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '4px 10px', cursor: 'pointer',
                      fontFamily: 'var(--m-font)', fontSize: 11,
                      color: on ? C.primary : C.text,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--m-panel-hi)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <span style={{ width: 8, color: C.primary }}>{on ? '✓' : ''}</span>
                    {o.label}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {multiCount > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', background: C.selBar,
          borderBottom: `1px solid ${C.border}`,
          color: C.primary, fontFamily: 'var(--m-font)', fontSize: 11,
        }}>
          <span>{multiCount} selected</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowMovePicker(true)} style={selBarBtn}>Move to…</button>
          <button onClick={bulkDelete} style={{ ...selBarBtn, color: C.danger, borderColor: C.danger + '55' }}>Delete</button>
          <button onClick={() => setMulti(new Set())} style={selBarBtn}>Clear</button>
        </div>
      )}

      <div
        className="aos-thin-scroll"
        onContextMenu={e => {
          // Only fire for clicks on empty space, not on cards (cards
          // bubble their own contextmenu — currently a no-op, but stops
          // the grid-level menu from racing).
          if (e.target !== e.currentTarget) return
          e.preventDefault()
          onEmptyContextMenu?.(e.clientX, e.clientY, activePath)
        }}
        style={{
          flex: 1, overflowY: 'auto',
          padding: viewMode === 'details' ? '4px 0' : 14,
          display: 'grid',
          // auto-fill at the mode's cell width: a wide dock packs MORE columns
          // rather than stretching cells past the size the mode asked for.
          gridTemplateColumns: viewMode === 'details'
            ? '1fr'
            : `repeat(auto-fill, minmax(${metrics.cell}px, 1fr))`,
          gap: metrics.gap,
          alignContent: 'start',
        }}>
        {childFolders.map(f => (
          <FolderTile
            key={f.key}
            folder={f}
            metrics={metrics}
            onOpen={() => onNavigate?.(f.path)}
            onDropAsset={(assetId) => onDropAsset?.(assetId, f.path)}
          />
        ))}
        {visible.length === 0 && childFolders.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11, padding: 30, textAlign: 'center' }}>
            {over ? 'Drop to upload' :
             query ? `No assets matching "${query}"${searchAll ? '' : ' here (try Global)'}.` :
             'No assets here. Drag files in to upload.'}
          </div>
        ) : visible.map(a => (
          <AssetCard
            key={a.id}
            asset={a}
            metrics={metrics}
            onDelete={multi.size > 1 ? undefined : deleteSingle}
            onSelect={ev => handleCardClick(a, ev)}
            onContextMenu={(asset, x, y) => setCtxMenu({ asset, x, y })}
            onOpenGraph={onOpenGraph}
            onEdit={openPrefabEditor}
            onOpenBlueprintGraph={openBlueprintGraph}
            opening={openingId === a.id}
            selected={multi.has(a.id) || selectedAssetId === a.id}
            renaming={renamingId === a.id}
            onCommitRename={(newName) => commitRename(a, newName)}
            onCancelRename={() => setRenamingId(null)}
          />
        ))}
      </div>
      {ctxMenu && (
        <AssetContextMenu
          asset={ctxMenu.asset}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          onTest={testAsset}
          onEdit={openPrefabEditor}
          onOpenBlueprintGraph={openBlueprintGraph}
          onExportBlueprint={exportBlueprintCard}
          onCopyId={(a) => {
            const id = a.rawId ?? a.id
            try { navigator.clipboard?.writeText(String(id)) } catch {}
          }}
          onDelete={deleteSingle}
          onRename={(asset) => setRenamingId(asset.id)}
          onItemHistory={
            characterId && assetKindToSection(ctxMenu.asset)
              ? (a) => setItemHistory({ section: assetKindToSection(a), itemId: a.rawId, label: a.name })
              : undefined
          }
          onApply={(asset) => {
            // ENG-529: applies the env to liveTuneStore + auto-switches to
            // Live View. LiveTunePanel's dropdown re-reads from the store
            // so it reflects the change immediately.
            const url = applyEnvironment(asset)
            if (url) {
              setActionStatus({ phase: 'ok', text: `Applied ${asset.name} as environment` })
              setTimeout(() => setActionStatus(s => (s && s.phase === 'ok' ? null : s)), 1800)
            } else {
              setActionStatus({ phase: 'error', text: `Could not apply ${asset.name} — no file URL` })
              setTimeout(() => setActionStatus(s => (s && s.phase === 'error' ? null : s)), 4000)
            }
          }}
          meshSlots={ctxMenu.asset.kind === 'material' ? CustomAssetManager.listMaterialMapping() : null}
          onApplyToSlot={async (asset, slot) => {
            const slotLabel = `Element ${CustomAssetManager.listMaterialMapping().findIndex(e => e.meshIndex === slot.meshIndex)} (${slot.meshName ?? '?'})`
            setActionStatus({ phase: 'saving', text: `Applying ${asset.name} → ${slotLabel}…` })
            const mapping = CustomAssetManager.listMaterialMapping()
            const next = mapping.map(e => e.meshIndex === slot.meshIndex ? { ...e, materialId: asset.rawId ?? asset.id } : e)
            try {
              await CustomAssetManager.putMaterialMapping(next)
              reloadMaterialsEverywhere()
              setActionStatus({ phase: 'ok', text: `Applied ${asset.name} → ${slotLabel}` })
              setTimeout(() => setActionStatus(s => (s && s.phase === 'ok' ? null : s)), 1800)
            } catch (e) {
              setActionStatus({ phase: 'error', text: `Apply failed: ${e.message ?? e}` })
              setTimeout(() => setActionStatus(s => (s && s.phase === 'error' ? null : s)), 4000)
            }
          }}
        />
      )}
      {showMovePicker && (
        <FolderPickerModal
          title={`Move ${multi.size} asset${multi.size > 1 ? 's' : ''} to…`}
          onCancel={() => setShowMovePicker(false)}
          onPick={bulkMoveTo}
        />
      )}
      {actionStatus && <ActionStatusChip status={actionStatus} />}
      {itemHistory && (
        <ItemHistoryModal
          characterId={characterId}
          section={itemHistory.section}
          itemId={itemHistory.itemId}
          itemLabel={itemHistory.label}
          onClose={() => setItemHistory(null)}
        />
      )}
      {editingPrefab && (
        <PrefabEditModal
          prefab={editingPrefab}
          onClose={() => setEditingPrefab(null)}
        />
      )}
    </div>
  )
}

// Navigable subfolder tile shown in the grid alongside asset cards, so a
// parent folder reveals its child folders (Explorer / Unreal behavior) rather
// than only its direct files. Click to open; assets can be dropped onto it.
function FolderTile({ folder, onOpen, onDropAsset, metrics }) {
  const [over, setOver] = useState(false)
  // Folders scale with the mode like the assets do — a 64px asset grid beside a
  // 120px folder tile reads as two grids.
  const m = metrics ?? { thumb: 64, pad: 10, fontSize: 11, showMeta: true }
  const details = m.cell === null
  return (
    <div
      onClick={onOpen}
      onDoubleClick={onOpen}
      onDragOver={e => {
        if (e.dataTransfer.types?.includes('text/aos-asset-id')) { e.preventDefault(); setOver(true) }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        e.preventDefault(); e.stopPropagation(); setOver(false)
        const assetId = e.dataTransfer.getData('text/aos-asset-id')
        if (assetId) onDropAsset?.(assetId)
      }}
      title={`Open ${folder.name}`}
      style={{
        display: 'flex',
        flexDirection: details ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: details ? 'flex-start' : 'center',
        gap: details ? 10 : 6,
        minHeight: details ? undefined : m.thumb + 40,
        padding: details ? `${m.pad}px 12px` : m.pad,
        borderRadius: details ? 0 : 'var(--m-radius)', cursor: 'pointer',
        boxShadow: details ? 'none' : 'var(--m-shadow-card)',
        background: over ? 'var(--m-panel-hi, #1f2a5c)' : (details ? 'transparent' : 'var(--m-panel, #11131b)'),
        border: details ? 'none' : `1px solid ${over ? 'var(--m-accent, #4d5bff)' : C.border}`,
        borderBottom: details ? `1px solid ${C.border}` : undefined,
      }}
    >
      <svg width={Math.round(m.thumb * 0.7)} height={Math.round(m.thumb * 0.7)} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flex: 'none' }}>
        <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4l2 2h7A1.5 1.5 0 0 1 19 8.5v8A1.5 1.5 0 0 1 17.5 18h-13A1.5 1.5 0 0 1 3 16.5v-10Z"
          fill="#2a2f52" stroke="#7c84d6" strokeWidth="1.2" />
      </svg>
      <div style={{
        fontFamily: 'var(--m-font)', fontSize: m.fontSize, color: C.text,
        textAlign: details ? 'left' : 'center', flex: details ? 1 : undefined,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
      }}>
        {folder.name}
      </div>
      {m.showMeta && (
        <div style={{ fontFamily: 'var(--m-mono)', fontSize: 9, color: C.dim, flex: 'none' }}>
          {folder.count} item{folder.count === 1 ? '' : 's'}
        </div>
      )}
    </div>
  )
}

// Floating bottom-right chip used to signal in-flight or just-finished
// actions whose result isn't visible in the asset grid itself (e.g.
// "Apply material to slot" updates the avatar but doesn't touch the
// grid). Sticks to the standard saving/ok/error palette.
function ActionStatusChip({ status }) {
  const palette = status.phase === 'error'
    ? { bg: '#3a1a1f', border: '#f8717155', fg: '#fca5a5', icon: '⚠' }
    : status.phase === 'ok'
      ? { bg: '#0f2c1d', border: '#22c55e55', fg: '#86efac', icon: '✓' }
      : { bg: '#1a1d28', border: '#2a2c36', fg: '#bec2ff', icon: '⟳' }
  return (
    <div style={{
      position: 'absolute', bottom: 12, right: 14, zIndex: 1500,
      background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 999, padding: '6px 12px',
      color: palette.fg, fontFamily: 'var(--m-font)', fontSize: 11,
      display: 'flex', alignItems: 'center', gap: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,.35)',
      maxWidth: 380,
    }}>
      <span style={{
        display: 'inline-block',
        animation: status.phase === 'saving' ? 'aos-spin 1s linear infinite' : 'none',
      }}>{palette.icon}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{status.text}</span>
      <style>{`@keyframes aos-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

function Breadcrumb({ path, searchAll, onNavigate }) {
  // "(all folders)" overrides the breadcrumb when global search is on —
  // there's no single path to navigate to in that mode.
  if (searchAll) {
    return <div style={{ color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11 }}>(all folders)</div>
  }
  if (!path.length) {
    return <Crumb label="/" onClick={onNavigate ? () => onNavigate([]) : undefined} active />
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11 }}>
      <Crumb label="/" onClick={onNavigate ? () => onNavigate([]) : undefined} />
      {path.map((segment, i) => {
        const isLast = i === path.length - 1
        const target = path.slice(0, i + 1)
        return (
          <React.Fragment key={i}>
            <span style={{ color: C.dim, padding: '0 2px' }}>/</span>
            <Crumb
              label={segment}
              onClick={onNavigate ? () => onNavigate(target) : undefined}
              active={isLast}
            />
          </React.Fragment>
        )
      })}
    </div>
  )
}

function Crumb({ label, onClick, active }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        background: 'transparent', border: 'none',
        color: active ? C.text : C.dim,
        fontFamily: 'var(--m-font)', fontSize: 11,
        padding: '2px 4px', borderRadius: 2,
        cursor: onClick && !active ? 'pointer' : 'default',
        fontWeight: active ? 700 : 400,
      }}
      onMouseEnter={e => { if (onClick && !active) e.currentTarget.style.color = C.primary }}
      onMouseLeave={e => { if (onClick && !active) e.currentTarget.style.color = C.dim }}
    >{label}</button>
  )
}

const toggleStyle = (active) => ({
  // Active toggle = accent-soft wash + accent text (Studio), not a hard tint.
  background: active ? activeWash.background : 'transparent',
  border: `1px solid ${active ? 'var(--m-accent)' : C.border}`,
  color: active ? activeWash.color : C.dim,
  ...pill,
  padding: '3px 10px',
  fontFamily: 'var(--m-font)', fontSize: 10,
  cursor: 'pointer',
  letterSpacing: '.06em', textTransform: 'uppercase',
})

const selBarBtn = {
  background: 'transparent',
  border: `1px solid ${C.border}`,
  color: C.dim,
  ...pill,
  padding: '3px 12px',
  fontFamily: 'var(--m-font)', fontSize: 10,
  letterSpacing: '.06em', textTransform: 'uppercase',
  cursor: 'pointer',
}
